import { useMemo } from 'react';
import { Source, Layer } from 'react-map-gl';
import { useRunningStore } from '@/stores/useRunningStore';
import { getAqiColor } from '@/utils/routeArt';

export function RecordingRouteLayer() {
  const isRecording = useRunningStore((s) => s.isRecording);
  const route = useRunningStore((s) => s.recordingData.route);

  // Check if any points have AQI data
  const hasAqi = useMemo(() => route.some(p => p.aqi !== undefined), [route]);

  // Build GeoJSON: AQI-colored segments or single line
  const geojson = useMemo((): GeoJSON.FeatureCollection | GeoJSON.Feature => {
    if (!hasAqi || route.length < 2) {
      // Single line fallback (no AQI data)
      return {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: route.map(p => [p.lng, p.lat]),
        },
      };
    }

    // Multi-segment: each segment colored by AQI
    const features: GeoJSON.Feature[] = [];
    for (let i = 1; i < route.length; i++) {
      const aqi = route[i - 1].aqi ?? route[i].aqi;
      features.push({
        type: 'Feature',
        properties: { color: getAqiColor(aqi) },
        geometry: {
          type: 'LineString',
          coordinates: [
            [route[i - 1].lng, route[i - 1].lat],
            [route[i].lng, route[i].lat],
          ],
        },
      });
    }

    return { type: 'FeatureCollection', features };
  }, [route, hasAqi]);

  if (!isRecording || route.length < 2) return null;

  if (hasAqi) {
    // AQI-colored segments: use data-driven line-color
    return (
      <Source id="recording-route" type="geojson" data={geojson}>
        <Layer
          id="recording-route-glow"
          type="line"
          paint={{
            'line-color': ['get', 'color'],
            'line-width': 12,
            'line-opacity': 0.3,
            'line-blur': 8,
          }}
        />
        <Layer
          id="recording-route-main"
          type="line"
          paint={{
            'line-color': ['get', 'color'],
            'line-width': 4,
            'line-opacity': 0.9,
          }}
        />
      </Source>
    );
  }

  // Fallback: single solid color (no AQI data)
  return (
    <Source id="recording-route" type="geojson" data={geojson}>
      <Layer
        id="recording-route-glow"
        type="line"
        paint={{
          'line-color': '#10b981',
          'line-width': 12,
          'line-opacity': 0.3,
          'line-blur': 8,
        }}
      />
      <Layer
        id="recording-route-main"
        type="line"
        paint={{
          'line-color': '#10b981',
          'line-width': 4,
          'line-opacity': 0.9,
        }}
      />
      <Layer
        id="recording-route-dash"
        type="line"
        paint={{
          'line-color': '#06b6d4',
          'line-width': 2,
          'line-dasharray': [2, 4],
          'line-opacity': 0.6,
        }}
      />
    </Source>
  );
}
